import {
    Brokers,
    GetTradingAccountMarginRequest,
    TradingAccount,
    TradingAccountMargin,
    TradingAccounts
} from '../../proto/generated/trading-bridge'
import commonActions from '../../actions/common.actions'
import api, { currentAdapter } from './axiosClient'
import store, { AppDispatch } from '../../store'
import { setMarginForAccount, setTradingAccounts, UserStateType } from '../../reducer/user.reducer'
import { PRIMARY_TRADING_ACCOUNT_KEY } from '../../assets/constants'
import { MarginFromBrokerRequest, MarginFromBrokerResponse } from '../../proto/generated/order'
import { PositionRequest } from '../../proto/generated/position'
import { getOrders, getPositions } from './orders'
import { updatePositions } from '../../reducer/positions.reducer'
import { setOrderHistory } from '../../reducer/orders.reducer'
import { isBrokerSessionReady, resolveOrderTradingAccountId } from '../../util/orderPlacement'
import { providerCapabilities } from '../../util/backendProvider'

const emptyTradingAccounts = (): TradingAccounts => ({ tradingAccounts: [] })

export const getBrokers = async (): Promise<Brokers> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return { brokers: [] }
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/brokers`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = Brokers.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getTradingAccounts = async (): Promise<TradingAccounts> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return emptyTradingAccounts()
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const removeTradingAccount = async (args: { body: any }): Promise<TradingAccounts> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return emptyTradingAccounts()
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts`
        api.delete(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            data: JSON.stringify(args.body),
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

// export const getBrokerLoginUrl = async (args: { body: GetBrokerLoginUrlRequest }): Promise<GetBrokerLoginUrlResponse> => {
//     return new Promise((resolve, reject) => {
//         const url = `/trading-accounts/login-url`
//         api.post(url, JSON.stringify(args.body), {
//             headers: {
//                 'Accept': 'application/x-protobuf'
//             },
//             responseType: 'arraybuffer'
//         }).then(async (response) => {
//             const decodedData = GetBrokerLoginUrlResponse.decode(new Uint8Array(response.data))
//             resolve(decodedData)
//         }).catch((error) => {
//             reject(error)
//         })
//     })
// }

export const getBrokerLoginUrl = async (_args: { body: any }): Promise<{ loginUrl: string }> => {
    return { loginUrl: '' }
}

export const getAccountMargins = async (args: { body: any }): Promise<TradingAccountMargin> => {
    if (!providerCapabilities.accountMargins) {
        const accountId = args.body?.accountId as string | undefined
        const margins = (store.getState().userStore as UserStateType)?.margins
        if (accountId && margins?.[accountId]) {
            return margins[accountId]
        }
        return TradingAccountMargin.create({})
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/margins`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccountMargin.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const syncAccountMarginFromBroker = async (args: { body: GetTradingAccountMarginRequest }): Promise<TradingAccountMargin> => {
    return currentAdapter.syncTradingAccountMargin(args.body)
}

export const getRequiredMarginFromBroker = async (args: {request: MarginFromBrokerRequest}): Promise<MarginFromBrokerResponse> => {
    return currentAdapter.getRequiredMargin(args.request)
}

export const connectTradingAccount = async (args: {
    body: any,
    dispatch: AppDispatch
}): Promise<{ tradingAccount?: TradingAccount }> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return {}
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/connect`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/json'
            },
            responseType: 'json'
        }).then(async (response) => {
            resolve(response.data as { tradingAccount?: TradingAccount })
        }).catch((error) => {
            commonActions.setApiError(args.dispatch, {
                error: true,
                message: 'Failed connecting trading account',
                errorType: 'error'
            })
            reject(error)
        })
    })
}

export const fetchBrokerPositionsAndOrders = async (
    primaryTradingAccount?: TradingAccount | null
) => {
    if (!isBrokerSessionReady(primaryTradingAccount)) {
        return
    }
    const accountId = resolveOrderTradingAccountId(primaryTradingAccount)
    const positionRequest = { tradingAccountId: accountId } as PositionRequest
    getPositions({ body: { positionRequest } })
        .then((response) => {
            store.dispatch(updatePositions({ positions: response.position, overwrite: true }))
        })
        .catch((error) => {
            console.error('Error fetching positions', error)
        })
    getOrders({ body: { tradingAccountId: accountId } })
        .then((response) => {
            const orders = response.reverse()
            store.dispatch(setOrderHistory({ updates: orders }))
        })
        .catch((error) => {
            console.error('Error fetching orders', error)
        })
}

export const setAppPrimaryTradingAccount = async (tradingAccountId: string) => {
    let tradingAccounts = (store.getState().userStore as UserStateType)?.tradingAccounts ?? []
    const primaryIndex = tradingAccounts.findIndex((acc) => acc.accountId === tradingAccountId)
    if (primaryIndex > -1) {
        tradingAccounts = tradingAccounts.map((acc, index) => {
            if (index === primaryIndex) {
                return { ...acc, primary: true }
            } else {
                return { ...acc, primary: false }
            }
        })
    } else if (providerCapabilities.tradingAccountDirectory) {
        tradingAccounts = (await getTradingAccounts()).tradingAccounts
        tradingAccounts = tradingAccounts.map((acc, index) => {
            if (index === primaryIndex) {
                return { ...acc, primary: true }
            } else {
                return { ...acc, primary: false }
            }
        })
    }
    refetchPositionsAndOrders(tradingAccounts, true)
}

let positionsFetchInFlight: Promise<void | undefined> | null = null
let positionsLoadedForAccountId: string | null = null

export const markPositionsLoaded = (accountId: string | undefined) => {
    if (accountId) positionsLoadedForAccountId = accountId
}

export const refetchPositionsAndOrders = async (alreadyTradingAccounts?: TradingAccount[], force = false) => {
    if (!force && positionsFetchInFlight) return positionsFetchInFlight
    const task = (async () => {
        let tradingAccounts = alreadyTradingAccounts
            ?? (store.getState().userStore as UserStateType)?.tradingAccounts
        let primaryTradingAccount = tradingAccounts?.find((acc) => acc.primary)
        if (!primaryTradingAccount && !providerCapabilities.tradingAccountDirectory) {
            await fetchBrokerPositionsAndOrders(null)
            return
        }
        if (!primaryTradingAccount) {
            try {
                const response = await getTradingAccounts()
                tradingAccounts = response.tradingAccounts
                primaryTradingAccount = tradingAccounts.find((acc) => acc.primary)
                store.dispatch(setTradingAccounts(tradingAccounts))
            } catch (error) {
                console.error('Failed to fetch trading accounts', error)
                return
            }
        }
        if (!primaryTradingAccount) {
            if (!providerCapabilities.tradingAccountDirectory) {
                await fetchBrokerPositionsAndOrders(null)
            }
            return
        }
        if (providerCapabilities.brokerSessionToken && !isBrokerSessionReady(primaryTradingAccount)) {
            return
        }
        const accountId = primaryTradingAccount.accountId
        if (!force && positionsLoadedForAccountId === accountId) return
        positionsLoadedForAccountId = accountId
        const positionRequest = { tradingAccountId: accountId } as PositionRequest
        const [positionsResult, ordersResult] = await Promise.allSettled([
            getPositions({ body: { positionRequest } }),
            getOrders({ body: { tradingAccountId: accountId } })
        ])
        if (positionsResult.status === 'fulfilled') {
            store.dispatch(updatePositions({ positions: positionsResult.value.position, overwrite: true }))
        } else {
            positionsLoadedForAccountId = null
            console.error('Error fetching positions', positionsResult.reason)
        }
        if (ordersResult.status === 'fulfilled') {
            const orders = ordersResult.value.reverse()
            store.dispatch(setOrderHistory({ updates: orders }))
        } else {
            positionsLoadedForAccountId = null
            console.error('Error fetching orders', ordersResult.reason)
        }
    })()
    positionsFetchInFlight = task.finally(() => {
        positionsFetchInFlight = null
    })
    return positionsFetchInFlight
}

export const resetPracticeTradingAccount = async (tradingAccountId: string): Promise<TradingAccounts> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return emptyTradingAccounts()
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/reset-practice`
        api.post(url, JSON.stringify({ tradingAccountId }), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            store.dispatch(setTradingAccounts(decodedData.tradingAccounts))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const setPracticeTradeEnabled = async (
    enabled: boolean,
    tradingAccountId?: string
): Promise<TradingAccounts> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return emptyTradingAccounts()
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/practice-trade/enabled`
        const body: { enabled: boolean; tradingAccountId?: string } = { enabled }
        if (tradingAccountId) {
            body.tradingAccountId = tradingAccountId
        }
        api.post(url, JSON.stringify(body), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            store.dispatch(setTradingAccounts(decodedData.tradingAccounts))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const setPrimaryTradingAccountApi = async (args: {
    body: any
}): Promise<TradingAccounts> => {
    if (!providerCapabilities.tradingAccountDirectory) {
        return emptyTradingAccounts()
    }
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/make-primary`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            store.dispatch(setTradingAccounts(decodedData.tradingAccounts))
            const primaryTradingAccount = decodedData.tradingAccounts.find(acc => acc.primary)
            if (primaryTradingAccount) {
                sessionStorage.setItem(PRIMARY_TRADING_ACCOUNT_KEY, primaryTradingAccount.accountId)
                const body = {
                    accountId: primaryTradingAccount.accountId,
                    brokerCode: primaryTradingAccount.brokerCode,
                    created: primaryTradingAccount.created
                }
                const margin = await getAccountMargins({
                    body: body
                })
                store.dispatch(setMarginForAccount({ accountId: primaryTradingAccount.accountId, margin: margin }))
                await refetchPositionsAndOrders(decodedData.tradingAccounts, true)
            }
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}